// @flow

import * as React from 'react'
import { saveAs } from 'file-saver'
import prettyBytes from 'pretty-bytes'
import { makeStyles } from '@material-ui/core/styles'
import {
  Chip,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  CircularProgress,
  Tooltip,
} from '@material-ui/core'
import { Description as FileIcon } from '@material-ui/icons'

import { getAsset } from 'services/cp-hcms'

const mediaHeight = 200

const useStyles = makeStyles((theme) => ({
  card: {
    position: 'relative',
  },
  chip: {
    position: 'absolute',
    top: theme.spacing(3),
    left: theme.spacing(3),
  },
  media: {
    backgroundColor: theme.palette.background.default,
    height: mediaHeight,
  },
  icon: {
    fontSize: 100,
  },
}))

type Props = {
  integration: IntegrationCPHCMS,
  assetId: string,
}

function ContentItemAsset({ assetId, integration }: Props) {
  const classes = useStyles()

  const [state, setState] = React.useState({
    isFetching: true,
    error: null,
    asset: null,
  })

  React.useEffect(() => {
    let ignore = false
    const fetchContentType = async () => {
      let asset = null
      let error = null

      try {
        asset = await getAsset({
          assetId,
          integration,
        })
      } catch (e) {
        error = e
      }

      if (!ignore) {
        setState({
          isFetching: false,
          asset,
          error,
        })
      }
    }
    fetchContentType()

    return () => {
      ignore = true
    }
  }, [assetId, integration])

  if (state.error) {
    return (
      <>
        <Typography align="center" variant="h2" color="error" paragraph>
          Error Loading Asset
        </Typography>
        <Typography align="center" paragraph>
          {state.error.message}
        </Typography>
        <Typography align="center" color="textSecondary">
          Re-opening this dialog to try again. If the problem persists, please
          contact support.
        </Typography>
      </>
    )
  }

  if (state.isFetching) {
    return (
      <Grid container justify="center">
        <CircularProgress />
      </Grid>
    )
  }

  const asset = state.asset
  if (!asset) {
    return (
      <Typography align="center" color="textSecondary">
        Asset does not exist: {assetId}.
      </Typography>
    )
  }

  return (
    <Card className={classes.card}>
      <CardActionArea
        onClick={() =>
          saveAs(
            `${integration.configuration.baseUrl}/api/assets/${assetId}?download=1`,
            asset.slug
          )
        }
      >
        {asset.isImage ? (
          <CardMedia
            className={classes.media}
            image={`${integration.configuration.baseUrl}/api/assets/${assetId}?height=${mediaHeight}`}
            alt={asset.slug}
          />
        ) : (
          <Grid
            container
            className={classes.media}
            justify="center"
            alignItems="center"
          >
            <FileIcon color="primary" className={classes.icon} />
          </Grid>
        )}
        <CardContent>
          <Tooltip title={asset.fileName}>
            <Typography gutterBottom variant="subtitle1" noWrap>
              {asset.fileName}
            </Typography>
          </Tooltip>
          <Typography variant="body2" color="textSecondary">
            {asset.pixelHeight && asset.pixelWidth ? (
              <span>
                {asset.pixelHeight}px by {asset.pixelWidth}px,{' '}
              </span>
            ) : null}
            {prettyBytes(asset.fileSize)}
          </Typography>
        </CardContent>
      </CardActionArea>
      <Chip
        color="primary"
        size="small"
        label={asset.fileExtension}
        className={classes.chip}
      />
    </Card>
  )
}

export default React.memo<Props>(ContentItemAsset)
